import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { fetchSmartMeterGateway } from '../api'
import { SmartMeterGatewayModel } from '../model'
import { smartMeterGatewayQueryKey } from './use-smart-meter-gateway-data'

interface SmartMeterGatewayDataForStoragesHook {
  isLoading: boolean
  smartMeterGatewayDataByStorageId: Record<string, SmartMeterGatewayModel | undefined>
  errorByStorageId: Record<string, Error | AuthFetchError | undefined>
  triggerSmartMeterGatewayDataFetch: (storageIds: string[]) => Promise<void>
}

const useSmartMeterGatewayDataForStorages = (): SmartMeterGatewayDataForStoragesHook => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const [smartMeterGatewayDataByStorageId, setSmartMeterGatewayDataByStorageId] = useState<Record<string, SmartMeterGatewayModel | undefined>>({})
  const [errorByStorageId, setErrorByStorageId] = useState<Record<string, Error | AuthFetchError | undefined>>({})

  const triggerSmartMeterGatewayDataFetch = useCallback(
    async (storageIds: string[]): Promise<void> => {
      const uniqueStorageIds = [...new Set(storageIds.filter(Boolean))]
      setIsLoading(true)

      const results = await Promise.all(
        uniqueStorageIds.map(async storageId => {
          try {
            const data = await queryClient.fetchQuery({
              queryKey: smartMeterGatewayQueryKey(storageId),
              queryFn: async () => {
                const result = await fetchSmartMeterGateway(storageId, authFetch)
                if (result.hasErrors) throw result.error ?? new Error('Could not fetch smart meter gateway data')
                return result.value
              },
            })
            return { storageId, data: data ?? undefined }
          } catch (error) {
            return { storageId, data: undefined, error: error as Error | AuthFetchError }
          }
        })
      )

      setSmartMeterGatewayDataByStorageId(Object.fromEntries(results.map(({ storageId, data }) => [storageId, data])))
      setErrorByStorageId(Object.fromEntries(results.map(({ storageId, error }) => [storageId, error])))
      setIsLoading(false)
    },
    [queryClient] //eslint-disable-line react-hooks/exhaustive-deps
  )

  return {
    isLoading,
    smartMeterGatewayDataByStorageId,
    errorByStorageId,
    triggerSmartMeterGatewayDataFetch,
  }
}

export default useSmartMeterGatewayDataForStorages
